import { loadStripe, Stripe } from '@stripe/stripe-js';
import { BillingInfo } from '../types/common';
import { pricingService } from './pricingService';
import { PricingPlan } from '../types/pricing';
import { Subscription } from '../types/subscription';
import { api } from './api';

// Types
export interface PaymentIntent {
  client_secret: string;
  payment_id: number;
  stripe_payment_intent_id: string;
  amount: number;
  currency: string;
}

// Re-export Subscription type for backward compatibility
export type { Subscription };

export interface Payment {
  id: number;
  user_id: number;
  stripe_payment_intent_id?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'canceled';
  payment_method?: string;
  description?: string;
  created_at: string;
  updated_at?: string;
}

export interface ComplianceAudit {
  id: number;
  user_id: number;
  audit_type: 'iso27001' | 'iso22301' | 'custom';
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  score?: number;
  findings?: string;
  recommendations?: string;
  auditor_id?: number;
  audit_date?: string;
  next_audit_date?: string;
  created_at: string;
  updated_at?: string;
}

export interface ComplianceDashboard {
  user_id: number;
  current_audit?: ComplianceAudit;
  active_subscription?: Subscription;
  recent_payments: Payment[];
  compliance_score?: number;
  next_audit_date?: string;
  recommendations: string[];
}

export interface ComplianceAssessment {
  audit_id: number;
  compliance_score: number;
  findings: Record<string, any>;
  recommendations: string[];
  report: string;
  next_assessment_date: string;
}

class PaymentService {
  private stripe: Stripe | null = null;
  private pricingPlans: PricingPlan[] = [];
  private readonly API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  constructor() {
    this.initializeStripe();
  }

  private async initializeStripe() {
    const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (stripePublishableKey) {
      this.stripe = await loadStripe(stripePublishableKey);
    }
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Route through the shared axios `api` instance so we get the auth header
    // injection + 401 -> token refresh interceptor for free.
    const method = (options.method || 'GET').toLowerCase();
    let data: unknown = undefined;
    if (options.body != null) {
      // Existing call sites all pass JSON.stringify(...) so this is a string.
      // Defensive: only parse strings; pass other shapes through untouched.
      if (typeof options.body === 'string') {
        try {
          data = JSON.parse(options.body);
        } catch {
          data = options.body;
        }
      } else {
        data = options.body;
      }
    }

    try {
      const response = await api.request<T>({
        url: endpoint,
        method: method as any,
        data,
      });
      return response.data;
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? err?.message ?? 'Request failed';
      const message = typeof detail === 'string' ? detail : JSON.stringify(detail);
      throw new Error(message);
    }
  }

  // Payment Methods
  async createSetupIntent(): Promise<{ client_secret: string }> {
    return this.makeRequest<{ client_secret: string }>('/v1/payment/create-setup-intent', {
      method: 'POST',
    });
  }

  async createSubscriptionWithPaymentMethod(
    planType: 'essential' | 'gold' | 'premium',
    paymentMethodId: string,
    addonType?: string
  ): Promise<Subscription> {
    let url = `/v1/payment/create-subscription-with-payment-method?plan_type=${planType}&payment_method_id=${paymentMethodId}`;
    if (addonType) url += `&addon_type=${addonType}`;
    return this.makeRequest<Subscription>(url, { method: 'POST' });
  }

  async createCheckoutSession(planType: 'essential' | 'gold' | 'premium', addonType?: string): Promise<{ checkout_url: string }> {
    let url = `/v1/payment/create-checkout-session?plan_type=${planType}`;
    if (addonType) url += `&addon_type=${addonType}`;
    return this.makeRequest<{ checkout_url: string }>(url, { method: 'POST' });
  }

  async getCheckoutSession(sessionId: string): Promise<{
    status: string;
    subscription?: Subscription;
    session?: any;
    standalone_tabletop?: boolean;
    credits_remaining?: number;
  }> {
    return this.makeRequest<{
      status: string;
      subscription?: Subscription;
      session?: any;
      standalone_tabletop?: boolean;
      credits_remaining?: number;
    }>(`/v1/payment/checkout-session/${sessionId}`);
  }

  // ==========================================================================
  // Standalone Tabletop ($500 = credits_per_pack from GET /tabletop/credits)
  // ==========================================================================

  async createStandaloneTabletopCheckoutSession(opts?: { returnTo?: string }): Promise<{
    checkout_url: string;
    session_id: string;
    amount_cents: number;
    credits: number;
  }> {
    return this.makeRequest<{
      checkout_url: string;
      session_id: string;
      amount_cents: number;
      credits: number;
    }>('/v1/payment/tabletop/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ return_to: opts?.returnTo }),
    });
  }

  async getStandaloneTabletopCredits(): Promise<{
    credits_remaining: number;
    credits_per_pack?: number;
    max_stocked_credits?: number;
    purchases: Array<{
      id: number;
      credits_total: number;
      credits_remaining: number;
      amount_cents: number;
      currency: string;
      created_at: string;
      paid_at?: string | null;
      last_used_at?: string | null;
    }>;
  }> {
    return this.makeRequest('/v1/payment/tabletop/credits');
  }



  async getPayments(): Promise<Payment[]> {
    return this.makeRequest<Payment[]>('/v1/payment/payments');
  }

  async getPaymentHistory(): Promise<Payment[]> {
    return this.makeRequest<Payment[]>('/v1/payment/payments');
  }

  // Subscription Methods
  async createSubscription(planType: 'essential' | 'gold' | 'premium', paymentMethodId: string, paymentIntentId?: string): Promise<Subscription> {
    return this.makeRequest<Subscription>('/v1/payment/create-subscription', {
      method: 'POST',
      body: JSON.stringify({
        plan_type: planType,
        payment_method_id: paymentMethodId,
        stripe_payment_intent_id: paymentIntentId
      }),
    });
  }

  async getSubscriptions(): Promise<Subscription[]> {
    return this.makeRequest<Subscription[]>('/v1/payment/subscriptions');
  }

  async getCurrentSubscription(): Promise<Subscription | null> {
    try {
      const subscriptions = await this.makeRequest<Subscription[]>('/v1/payment/subscriptions');
      // Return the first active subscription, or null if none found
      return subscriptions.find(sub => sub.status === 'active') || null;
    } catch (error) {
      console.error('Error fetching current subscription:', error);
      return null;
    }
  }

  async cancelSubscription(subscriptionId: number): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`/v1/payment/cancel-subscription/${subscriptionId}`, {
      method: 'POST',
    });
  }

  async resumeSubscription(subscriptionId: number): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`/v1/payment/resume-subscription/${subscriptionId}`, {
      method: 'POST',
    });
  }

  async changeSubscriptionPlan(subscriptionId: number, newPlanType: 'essential' | 'gold' | 'premium'): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`/v1/payment/change-subscription-plan/${subscriptionId}?new_plan_type=${newPlanType}`, {
      method: 'POST',
    });
  }

  async createBillingPortalSession(): Promise<{ url: string }> {
    return this.makeRequest<{ url: string }>('/v1/payment/billing-portal');
  }

  // Compliance Methods
  async createComplianceAssessment(businessData: Record<string, unknown>): Promise<ComplianceAssessment> {
    return this.makeRequest<ComplianceAssessment>('/v1/payment/compliance-assessment', {
      method: 'POST',
      body: JSON.stringify(businessData),
    });
  }

  async getComplianceAudits(): Promise<ComplianceAudit[]> {
    return this.makeRequest<ComplianceAudit[]>('/v1/payment/compliance-audits');
  }

  async getComplianceDashboard(): Promise<ComplianceDashboard> {
    return this.makeRequest<ComplianceDashboard>('/v1/payment/compliance-dashboard');
  }

  async updateComplianceAudit(auditId: number, updates: Partial<ComplianceAudit>): Promise<ComplianceAudit> {
    return this.makeRequest<ComplianceAudit>(`/v1/payment/compliance-audit/${auditId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Billing Methods
  async getBillingInfo(): Promise<any> {
    return this.makeRequest<any>('/v1/payment/billing-info');
  }

  async updateBillingInfo(billingInfo: BillingInfo): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>('/v1/payment/billing-info', {
      method: 'PUT',
      body: JSON.stringify(billingInfo),
    });
  }

  async getPaymentMethods(): Promise<any[]> {
    return this.makeRequest<any[]>('/v1/payment/payment-methods');
  }

  async addPaymentMethod(paymentMethodId: string): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>('/v1/payment/payment-methods', {
      method: 'POST',
      body: JSON.stringify({ payment_method_id: paymentMethodId }),
    });
  }

  async removePaymentMethod(paymentMethodId: string): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`/v1/payment/payment-methods/${paymentMethodId}`, {
      method: 'DELETE',
    });
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<{ message: string }> {
    return this.makeRequest<{ message: string }>(`/v1/payment/payment-methods/${paymentMethodId}/default`, {
      method: 'POST',
    });
  }

  // Utility Methods
  getStripe(): Stripe | null {
    return this.stripe;
  }

  formatCurrency(amount: number, currency: string = 'usd'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  }

  getPlanFeatures(planType: 'essential' | 'gold' | 'premium'): string[] {
    return pricingService.getPlanFeatures(planType, this.pricingPlans);
  }

  async initializePricing(): Promise<void> {
    try {
      this.pricingPlans = await pricingService.getPricingPlans();
    } catch (error) {
      console.error('Failed to load pricing plans:', error);
      // Fallback to default pricing
      this.pricingPlans = [];
    }
  }

  getPlanPrice(planType: 'essential' | 'gold' | 'premium'): number {
    return pricingService.getPlanPrice(planType, this.pricingPlans);
  }
}

export const paymentService = new PaymentService();
export default paymentService;
